import { motion } from 'framer-motion';

interface ScoreTrendProps {
  scores: { score: number; date: string }[];
}

export default function ScoreTrend({ scores }: ScoreTrendProps) {
  const data = scores.slice(-10);

  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center py-8 text-cream/40 text-sm">
        Not enough data to show a trend
      </div>
    );
  }

  const W = 500;
  const H = 200;
  const PAD = { top: 20, right: 20, bottom: 40, left: 40 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const xStep = plotW / (data.length - 1);
  const toX = (i: number) => PAD.left + i * xStep;
  const toY = (v: number) => PAD.top + plotH - (v / 10) * plotH;

  const points = data.map((d, i) => `${toX(i)},${toY(d.score)}`).join(' ');

  const gridLines = [0, 2.5, 5, 7.5, 10];

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Gridlines */}
      {gridLines.map((v) => (
        <g key={v}>
          <line
            x1={PAD.left} y1={toY(v)} x2={W - PAD.right} y2={toY(v)}
            stroke="#3d4a34" strokeWidth={1} strokeDasharray={v === 0 || v === 10 ? '0' : '4 4'}
          />
          <text x={PAD.left - 8} y={toY(v) + 4} textAnchor="end" fill="#8aad7a" fontSize={11}>
            {v}
          </text>
        </g>
      ))}

      {/* Data line — progressive draw */}
      <motion.polyline
        points={points}
        fill="none"
        stroke="#4ade80"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.5, ease: 'easeInOut' }}
      />

      {/* Data dots and x-labels */}
      {data.map((d, i) => (
        <g key={i}>
          <motion.circle
            cx={toX(i)} cy={toY(d.score)} r={4} fill="#4ade80"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: (i / data.length) * 1.5 + 0.2, duration: 0.3 }}
          />
          <circle cx={toX(i)} cy={toY(d.score)} r={2} fill="#1a1f16" />
          <text
            x={toX(i)}
            y={H - 8}
            textAnchor="middle"
            fill="#a8c89a"
            fontSize={10}
          >
            {fmtDate(d.date)}
          </text>
        </g>
      ))}
    </svg>
  );
}
